import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/format";
import { toast } from "sonner";
import { Users, Copy, Check, UserPlus, LogOut, Trash2, Crown } from "lucide-react";

export default function HouseholdPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { setData((await api.get("/household")).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const generateCode = async () => {
    setLoading(true);
    try { await api.post("/household/invite"); await load(); toast.success("Código generado"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const joinByCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    try {
      await api.post("/household/join", { code: code.trim() });
      toast.success("¡Te uniste al hogar!");
      setCode("");
      await load();
      window.location.reload();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const leave = async () => {
    if (!window.confirm("¿Salir del hogar familiar? Crearás tu propio hogar individual.")) return;
    try { await api.post("/household/leave"); toast.success("Has salido del hogar"); window.location.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeMember = async (mid) => {
    if (!window.confirm("¿Remover este miembro del hogar?")) return;
    try { await api.delete(`/household/members/${mid}`); toast.success("Miembro removido"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isOwner = data && user && data.owner_id === user.id;
  const memberOfOther = data && user && data.owner_id !== user.id;

  return (
    <div className="space-y-6" data-testid="household-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Familia</div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">Hogar compartido</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Invita a tu familia o pareja para compartir las finanzas del hogar. Todos los miembros ven y gestionan las mismas cuentas, gastos y presupuestos.
        </p>
      </div>

      {/* Invite section */}
      {isOwner && (
        <section className="card-surface p-6" data-testid="invite-section">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-violet-400"/>
            <h2 className="font-semibold">Invitar a tu hogar</h2>
          </div>
          {data?.invite?.code ? (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Código de invitación</div>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <div className="text-3xl font-bold tracking-[0.2em] bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent" data-testid="invite-code">
                  {data.invite.code}
                </div>
                <button onClick={() => copy(data.invite.code)} data-testid="copy-code-btn"
                  className="flex items-center gap-2 text-sm bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg border border-white/10">
                  {copied ? <Check className="w-4 h-4 text-emerald-400"/> : <Copy className="w-4 h-4"/>}
                  {copied ? "Copiado" : "Copiar"}
                </button>
                <button onClick={generateCode} disabled={loading} data-testid="regen-code-btn"
                  className="text-sm text-slate-400 hover:text-violet-300">Regenerar</button>
              </div>
              <div className="mt-3 text-xs text-slate-500">Comparte este código con miembros de tu familia. Ellos lo ingresan al iniciar sesión para unirse.</div>
            </div>
          ) : (
            <button onClick={generateCode} disabled={loading} data-testid="generate-code-btn"
              className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-5 py-2.5 rounded-xl font-medium">
              <UserPlus className="w-4 h-4"/> Generar código de invitación
            </button>
          )}
        </section>
      )}

      {/* Join section */}
      {!memberOfOther && (
        <section className="card-surface p-6" data-testid="join-section">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-violet-400"/>
            <h2 className="font-semibold">¿Tienes un código?</h2>
          </div>
          <p className="text-sm text-slate-400 mb-4">Si un familiar te invitó a su hogar, ingresa su código para unirte.</p>
          <form onSubmit={joinByCode} className="flex gap-2 flex-wrap">
            <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="EJ: A7B2C9X1" data-testid="join-code-input"
              className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-lg font-mono tracking-widest placeholder:text-slate-600"/>
            <button type="submit" data-testid="join-btn"
              className="bg-violet-500/15 hover:bg-violet-500/25 text-violet-200 border border-violet-500/30 px-5 py-3 rounded-xl font-medium">Unirme</button>
          </form>
        </section>
      )}

      {/* Members */}
      <section data-testid="members-section">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-violet-400"/>
          <h2 className="font-semibold">Miembros del hogar</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {(data?.members || []).map(m => (
            <div key={m.id} className="card-surface p-4 flex items-center gap-3" data-testid={`member-${m.id}`}>
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-white font-semibold">
                {m.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  {m.name}
                  {m.id === data.owner_id && <Crown className="w-3.5 h-3.5 text-amber-400" title="Dueño"/>}
                </div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </div>
              {isOwner && m.id !== data.owner_id && (
                <button onClick={() => removeMember(m.id)} className="text-slate-500 hover:text-rose-400 p-2" data-testid={`remove-member-${m.id}`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              )}
            </div>
          ))}
        </div>
        {memberOfOther && (
          <button onClick={leave} data-testid="leave-household-btn"
            className="mt-6 flex items-center gap-2 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/15 px-4 py-2.5 rounded-xl">
            <LogOut className="w-4 h-4"/> Salir del hogar
          </button>
        )}
      </section>
    </div>
  );
}
